import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchConsumerTopicMedia } from "../api/consumerMediaApi";
import { resolveTopicBackendIds } from "../utils/resolveTopicBackendIds";
import { isTopicContentAccessGranted } from "../utils/topicAccess";
import {
  filterRowsForTopic,
  isBenignEmptyContentMessage,
} from "../utils/topicContentMatch";

export { isTopicContentAccessGranted as isTopicMediaAccessGranted } from "../utils/topicAccess";

export function resolveMediaProviderName(item = {}) {
  return (
    item.service_provider_name ||
    item.provider_name ||
    item.company_name ||
    item.display_name ||
    item.providerName ||
    item.service_provider?.name ||
    item.service_provider?.display_name ||
    null
  );
}

export function normalizeConsumerMediaItem(raw = {}) {
  const mediaType = raw.media_type || raw.mediaType || "";
  return {
    id: raw.id,
    mediaType,
    title: raw.media_title || raw.title || "Untitled",
    description: raw.description || "",
    mediaUrl: raw.media_url || raw.mediaUrl || "",
    providerName: resolveMediaProviderName(raw),
    raw,
  };
}

export function partitionMediaByType(items = []) {
  const videos = [];
  const podcasts = [];

  items.forEach((item) => {
    const type = item.mediaType || item.media_type || "";
    if (type === "audio_podcast") {
      podcasts.push(item);
      return;
    }
    if (type === "video" || type === "video_podcast") {
      videos.push(item);
    }
  });

  return { videos, podcasts };
}

export function groupMediaByProvider(items = []) {
  const groups = new Map();
  items.forEach((item) => {
    const label = item.providerName || "Featured providers";
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(item);
  });
  return [...groups.entries()].map(([providerName, mediaItems]) => ({
    providerName,
    items: mediaItems,
  }));
}

/**
 * Loads topic media only when the visitor is signed in and has this subcategory in their interests.
 */
export function useTopicMedia({
  isAuthenticated,
  user,
  signupCategories,
  industryId,
  menuItem,
  enabled = true,
}) {
  const topicIds = useMemo(
    () => resolveTopicBackendIds(signupCategories, industryId, menuItem),
    [signupCategories, industryId, menuItem],
  );

  const canAccess = useMemo(
    () => isTopicContentAccessGranted(isAuthenticated, user, topicIds),
    [isAuthenticated, user, topicIds],
  );

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!canAccess || !topicIds?.subCategoryName || !user) {
      setItems([]);
      setError(null);
      setLoading(false);
      return;
    }

    const uid = user.id ?? user.user_id;
    const email = user.email ?? user.user_email ?? "";
    if (uid == null || !email.trim()) {
      setItems([]);
      setError("Could not read your account details.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await fetchConsumerTopicMedia({
      userId: uid,
      userEmail: email,
      subCategory: topicIds.subCategoryName,
    });

    if (!result.status) {
      const message = result.message || "Unable to load media.";
      setItems([]);
      setError(isBenignEmptyContentMessage(message) ? null : message);
    } else {
      const scoped = filterRowsForTopic(result.data, topicIds);
      setItems(scoped.map(normalizeConsumerMediaItem));
      setError(null);
    }
    setLoading(false);
  }, [canAccess, topicIds, user]);

  useEffect(() => {
    if (!enabled) return undefined;
    load();
    return undefined;
  }, [enabled, load]);

  const { videos, podcasts } = useMemo(() => partitionMediaByType(items), [items]);
  const totalCount = items.length;

  return {
    topicIds,
    canAccess,
    items,
    videos,
    podcasts,
    totalCount,
    loading,
    error,
    reload: load,
  };
}
